import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

interface CompanyData {
  id: string;
  name: string;
  logo_url: string;
  timezone: string;
  language: string;
  created_at: string;
}

const timezones = [
  'America/Sao_Paulo',
  'America/Rio_de_Janeiro',
  'America/Salvador',
  'America/Recife',
  'America/Fortaleza',
  'America/Belem',
  'America/Manaus',
  'America/Cuiaba',
  'America/Campo_Grande',
  'America/Buenos_Aires',
  'America/Lima',
  'America/Mexico_City',
  'America/New_York',
  'Europe/Lisbon',
  'Europe/London',
  'UTC',
];

const languages = [
  { value: 'pt-BR', label: 'Português (Brasil)' },
  { value: 'en-US', label: 'English (United States)' },
  { value: 'es-ES', label: 'Español (España)' },
];

export default function SettingsGeneralPage() {
  const { profile } = useAuth();
  const [company, setCompany] = useState<CompanyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [form, setForm] = useState({
    name: '',
    timezone: 'America/Sao_Paulo',
    language: 'pt-BR',
  });

  useEffect(() => {
    const fetchCompany = async () => {
      if (!profile?.company_id) {
        setLoading(false);
        return;
      }
      try {
        const { data, error } = await supabase
          .from('companies')
          .select('*')
          .eq('id', profile.company_id)
          .single();

        if (error) throw error;

        if (data) {
          setCompany(data as CompanyData);
          setForm({
            name: data.name || '',
            timezone: data.timezone || 'America/Sao_Paulo',
            language: data.language || 'pt-BR',
          });
        }
      } catch (err: unknown) {
        console.error('Error fetching company:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompany();
  }, [profile?.company_id]);

  const handleSave = async () => {
    if (!profile?.company_id) return;
    setSaving(true);
    setMessage(null);

    try {
      const { error } = await supabase
        .from('companies')
        .update({
          name: form.name,
          timezone: form.timezone,
          language: form.language,
          updated_at: new Date().toISOString(),
        })
        .eq('id', profile.company_id);

      if (error) throw error;

      setMessage({ type: 'success', text: 'Configuracoes salvas com sucesso!' });
    } catch (err: unknown) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Erro ao salvar configuracoes',
      });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-heading font-bold text-foreground-950">Configuracoes Gerais</h2>
        <p className="text-sm text-foreground-500 mt-1">Informacoes da empresa, fuso horario e preferencias do sistema</p>
      </div>

      {message && (
        <div className={`rounded-xl px-4 py-3 flex items-center gap-3 ${message.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          <i className={`${message.type === 'success' ? 'ri-check-line text-emerald-500' : 'ri-error-warning-line text-red-500'} text-lg`} />
          <p className="text-sm">{message.text}</p>
        </div>
      )}

      <div className="bg-background-100 rounded-2xl border border-background-200 p-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Nome da Empresa</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Minha Empresa LTDA"
              className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Fuso Horario</label>
            <select
              value={form.timezone}
              onChange={(e) => setForm((prev) => ({ ...prev, timezone: e.target.value }))}
              className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer"
            >
              {timezones.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Idioma</label>
            <select
              value={form.language}
              onChange={(e) => setForm((prev) => ({ ...prev, language: e.target.value }))}
              className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer"
            >
              {languages.map((l) => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-3 pt-3 border-t border-background-100">
          <button
            onClick={handleSave}
            disabled={saving || !form.name.trim()}
            className="px-5 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin" /> Salvando...
              </>
            ) : (
              <>
                <i className="ri-save-line" /> Salvar
              </>
            )}
          </button>
        </div>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-background-100 rounded-xl border border-background-200 p-4">
          <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center mb-3">
            <i className="ri-time-line text-primary-600 text-lg" />
          </div>
          <h4 className="text-sm font-semibold text-foreground-900 mb-1">Fuso Horario</h4>
          <p className="text-xs text-foreground-500">Define o horario local para todos os agendamentos e logs do sistema</p>
        </div>
        <div className="bg-background-100 rounded-xl border border-background-200 p-4">
          <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center mb-3">
            <i className="ri-global-line text-accent-600 text-lg" />
          </div>
          <h4 className="text-sm font-semibold text-foreground-900 mb-1">Idioma</h4>
          <p className="text-xs text-foreground-500">Idioma padrao da interface para todos os usuarios da empresa</p>
        </div>
        <div className="bg-background-100 rounded-xl border border-background-200 p-4">
          <div className="w-9 h-9 rounded-lg bg-secondary-50 flex items-center justify-center mb-3">
            <i className="ri-building-line text-secondary-600 text-lg" />
          </div>
          <h4 className="text-sm font-semibold text-foreground-900 mb-1">Identidade</h4>
          <p className="text-xs text-foreground-500">Nome da empresa exibido em relatórios, notificacoes e emails</p>
        </div>
      </div>
    </div>
  );
}